import 'react-quill-new/dist/quill.core.css';
import React, { useState, useEffect } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import { Button, Modal, Table, message } from 'antd';
import { useCart } from '../CartContext';
import './EventsSection.css';
import { FaMapMarkerAlt } from 'react-icons/fa';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';

function EventsSection() {
  const [events, setEvents] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [addedToCart, setAddedToCart] = useState(null);
  const { addToCart, cart, updateQuantity } = useCart();
  const { eventId } = useParams();
  const navigate = useNavigate();
  const [isCopied, setIsCopied] = useState(false);

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const querySnapshot = await getDocs(collection(db, 'events'));
        const eventsData = querySnapshot.docs
  .map((doc) => ({ id: doc.id, ...doc.data() }))
  .filter((e) => !e.archived);
        setEvents(eventsData);
      } catch (error) {
        console.error('Error fetching events:', error);
      }
    };
    fetchEvents();
  }, []);

  useEffect(() => {
    if (eventId) {
      const matchingEvent = events.find(
        (event) => createEventSlug(event.name) === eventId
      );

      if (matchingEvent) {
        setSelectedEvent(matchingEvent);
        setIsModalOpen(true);
      } else {
        const fetchEvent = async () => {
          const docRef = doc(db, 'events', eventId);
          const docSnap = await getDoc(docRef);
          if (docSnap.exists()) {
            setSelectedEvent({ id: docSnap.id, ...docSnap.data() });
            setIsModalOpen(true);
          }
        };
        fetchEvent();
      }
    }
  }, [eventId, events]);

  const showModal = (event) => {
    setSelectedEvent(event);
    setIsModalOpen(true);
  };

  const handleCancel = () => {
    setIsModalOpen(false);
    setSelectedEvent(null);
  };

  const createEventSlug = (name) => {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/(^_+|_+$)/g, '');
  };

  const copyEventLink = () => {
    const slug = createEventSlug(selectedEvent.name);
    const url = `${window.location.origin}/events/${slug}`;
    navigator.clipboard.writeText(url);
    setIsCopied(true);
    setTimeout(() => setIsCopied(false), 2000);
    message.success('Event link copied!');
  };

  const handleAddToCart = (ticket) => {
    addToCart({
      event: selectedEvent.name,
      ticketType: ticket.type,
      price: ticket.price,
      maxQty: ticket.qty ? parseInt(ticket.qty) - (ticket.sold ?? 0) : null,
    });
    setAddedToCart(ticket.type);
    message.success('Added to cart successfully!');
  };

  const isSoldOut = (t) => t.qty && (t.sold ?? 0) >= parseInt(t.qty);

  const isAtCartLimit = (t) => {
    if (!t.qty) return false;
    const remaining = parseInt(t.qty) - (t.sold ?? 0);
    const inCart = cart.find(
      (item) => item.event === selectedEvent?.name && item.ticketType === t.type
    );
    return (inCart?.quantity || 0) >= remaining;
  };

  const isEventSoldOut = (ev) => ev.tickets?.length && ev.tickets.every(t => isSoldOut(t));

  const getCartQty = (ticket) => {
    const item = cart.find(
      (i) => i.event === selectedEvent?.name && i.ticketType === ticket.type
    );
    return item?.quantity || 0;
  };

  const columns = [
    {
      title: 'Ticket Type',
      dataIndex: 'type',
      key: 'type',
      render: (type, record) => (
        <div>
          <div style={{ fontWeight: 'bold' }}>{type}</div>
          {isSoldOut(record) ? (
            <div style={{ color: '#ff4d4f', fontSize: '12px', marginTop: '4px', fontWeight: 'bold' }}>🚫 Sold Out</div>
          ) : getCartQty(record) > 0 ? (
            <div style={{ color: '#28a745', fontSize: '12px', marginTop: '4px', fontWeight: 'bold' }}>✅ In cart</div>
          ) : record.qty ? (
            <div style={{ color: '#28a745', fontSize: '12px', marginTop: '4px', fontWeight: 'bold' }}>
              ✅ {parseInt(record.qty) - (record.sold ?? 0)} left
            </div>
          ) : (
            <div style={{ color: '#28a745', fontSize: '12px', marginTop: '4px' }}>✅ Available</div>
          )}
        </div>
      ),
    },
    {
      title: 'Price',
      dataIndex: 'price',
      key: 'price',
      render: (price) => `$${parseFloat(price || 0).toFixed(2)}`,
    },
    {
      title: 'Add to Cart',
      key: 'action',
      render: (_, record) => {
        const qty = getCartQty(record);
        const maxQty = record.qty ? parseInt(record.qty) - (record.sold ?? 0) : 99;
        const inCartItem = cart.find(
          (i) => i.event === selectedEvent?.name && i.ticketType === record.type
        );

        if (isSoldOut(record)) {
          return (
            <button disabled style={{
              padding: '6px 12px', borderRadius: '6px',
              background: '#ddd', border: 'none', color: '#999',
              fontSize: '13px', cursor: 'not-allowed'
            }}>
              Sold Out
            </button>
          );
        }

        if (qty === 0) {
          return (
            <button
              onClick={() => handleAddToCart(record)}
              style={{
                padding: '6px 14px', borderRadius: '6px',
                background: '#2563eb', border: 'none', color: 'white',
                fontSize: '13px', fontWeight: 'bold', cursor: 'pointer',
                display: 'flex', alignItems: 'center', gap: '6px'
              }}
            >
              🛒 Add to Cart
            </button>
          );
        }

        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <button
              onClick={() => {
                if (qty - 1 === 0) updateQuantity(inCartItem, 0);
                else updateQuantity(inCartItem, qty - 1);
              }}
              style={{
                width: '28px', height: '28px', borderRadius: '6px',
                border: '1px solid #ddd', background: '#f0f0f0',
                color: '#333', cursor: 'pointer', fontSize: '16px',
                display: 'flex', alignItems: 'center', justifyContent: 'center'
              }}
            >−</button>
            <span style={{
              color: '#333', fontWeight: 'bold',
              minWidth: '20px', textAlign: 'center',
              background: 'white', padding: '2px 8px',
              borderRadius: '4px', fontSize: '13px',
              border: '1px solid #ddd'
            }}>{qty}</span>
            <button
              onClick={() => {
                if (qty < maxQty) updateQuantity(inCartItem, qty + 1);
              }}
              disabled={qty >= maxQty}
              style={{
                width: '28px', height: '28px', borderRadius: '6px',
                border: '1px solid #2563eb',
                background: qty >= maxQty ? '#f0f0f0' : '#2563eb',
                color: qty >= maxQty ? '#999' : 'white',
                cursor: qty >= maxQty ? 'not-allowed' : 'pointer',
                fontSize: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center'
              }}
            >+</button>
          </div>
        );
      },
    },
  ];

  return (
    <div className="events-section">
      <h2>Upcoming Events</h2>

      {events.length > 0 ? (
        <div className="events-grid">
          {events.map((event) => (
            <div key={event.id} className="event-card">
              <img
                src={event.images?.[0] || event.imageURL}
                alt={event.name}
                className="event-image"
                onClick={() =>
                  navigate(`/events/${createEventSlug(event.name)}`)
                }
                style={{ cursor: 'pointer' }}
              />
              <h3>{event.name}</h3>

              {event.location && (
                <div
                  className="event-location"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    margin: '10px 0',
                  }}
                >
                  <FaMapMarkerAlt
                    style={{ marginRight: '6px', color: 'red' }}
                  />
                  <span>{event.location}</span>
                </div>
              )}

              {event.datetime && (
                <div
                  className="event-datetime"
                  style={{
                    marginTop: '8px',
                    color: '#94a3b8',
                    fontSize: '14px',
                    paddingBottom: '8px',
                  }}
                >
                  📅 {event.datetime}
                </div>
              )}

              {isEventSoldOut(event) ? (
                <Button disabled style={{ marginTop: '10px', padding: '10px 20px', backgroundColor: '#ccc' }}>
                  🚫 Sold Out
                </Button>
              ) : (
                <Button type="primary" style={{ marginTop: '10px', padding: '10px 20px' }} onClick={() => showModal(event)}>
                  Buy Tickets
                </Button>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className="no-events-message">
          <p>No current events yet. Check back soon!</p>
        </div>
      )}

      {selectedEvent && (
        <Modal
          title={selectedEvent.name}
          open={isModalOpen}
          onCancel={handleCancel}
          footer={[
            <Button key="share" onClick={copyEventLink}>
              {isCopied ? 'Copied!' : 'Share Event'}
            </Button>,
            <Button key="close" onClick={handleCancel}>
              Close
            </Button>,
          ]}
        >
          <div className="event-images-stack">
            {(selectedEvent.images || [selectedEvent.imageURL])
              .filter(Boolean)
              .map((img, index) => (
                <img
                  key={index}
                  src={img}
                  alt={`Event ${index + 1}`}
                  className="event-modal-image"
                  style={{ cursor: 'default' }}
                />
              ))}
          </div>

          {selectedEvent.location && (
            <div
              className="event-location"
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: '10px',
              }}
            >
              <FaMapMarkerAlt style={{ marginRight: '8px', color: 'red' }} />
              <span>{selectedEvent.location}</span>
            </div>
          )}

          {selectedEvent.datetime && (
            <div
              className="event-datetime"
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginTop: '8px',
                marginBottom: '10px',
                color: '#555',
                fontSize: '14px',
              }}
            >
              📅 {selectedEvent.datetime}
            </div>
          )}

          <div 
  className="event-modal-description" 
  dangerouslySetInnerHTML={{ __html: selectedEvent.description.replace(/&nbsp;/g, ' ') }} 
/>

          <Table
            dataSource={selectedEvent.tickets}
            columns={columns}
            pagination={false}
            rowKey="type"
          />
        </Modal>
      )}
    </div>
  );
}

export default EventsSection;